import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { TranslocoService } from '@jsverse/transloco';
import { PAGE } from 'src/app/routes';
import { UxGameService } from '../ux-game/ux-game.service';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastController = inject(ToastController);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private uxGameService = inject(UxGameService);

  private isGoToMatchToastShown = false;

  /**
   * Displays a "copied" toast message.
   * @param message - The message to display.
   * @returns A promise that resolves when the toast is shown.
   */
  async copied(message: string): Promise<void> {
    return this.show(message, 'assets/icons/atlas/copy/16.svg');
  }

  /**
   * Displays a toast with a message and a button that navigates to a match page when clicked.
   * @param message - The message to display.
   * @param matchId - The ID of the match to navigate to.
   */
  async showGoToMatchToast(gameId: string, roomId: string): Promise<void> {
    if (this.isGoToMatchToastShown || this.router.url.includes('game')) {
      return;
    }

    const toast = await this.toastController.create({
      message: this.translocoService.translate('service.toast.go-to-match-message'),
      position: 'top',
      buttons: [
        {
          text: this.translocoService.translate('service.toast.go-to-match-button'),
          handler: async () => {
            if (!this.router.url.startsWith(`/${PAGE.GAME}`)) {
              await this.router.navigate([`${PAGE.GAME}/${gameId}`]);
            }
            this.uxGameService.launchPaidGame(roomId);

            this.isGoToMatchToastShown = false;
          },
        },
        {
          text: this.translocoService.translate('service.toast.close'),
          handler: () => {
            this.isGoToMatchToastShown = false;
          },
          role: 'cancel',
          cssClass: 'toast-cancel-button',
        },
      ],
      cssClass: 'toast',
    });
    this.isGoToMatchToastShown = true;
    await toast.present();
  }

  /**
   * Displays a toast message with the specified message and icon.
   * @param message - The message to display.
   * @param icon - The name of the icon to display.
   * @returns A promise that resolves when the toast is shown.
   */
  private async show(message: string, icon: string, duration = 1500): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration,
      position: 'top',
      buttons: [
        {
          side: 'start',
          icon,
        },
      ],
      cssClass: 'toast',
    });
    await toast.present();
  }
}
